import { memoize } from "lodash";
import { observable, runInAction } from "mobx";

import { apiCallGetMessageReactionHistory } from "../../../api/user-api/api-call-get-message-reaction-history";
import { Gender, TMessageReactionEvent } from "../../../Types";
import getCoreHistoryStore from "../../core/core-history-store";
import getCorePopUpStore from "../../core/core-popup-store";
import getUserConversationStore from "../conversation-store";
import getUserMessageIdStore from "../message-id-store";
import getUserMessagesStore from "../messages-store";

class ReactionHistoryStore {
  isVisible = observable.box<boolean>(false);
  isLoading = observable.box<boolean>(false);
  events = observable.box<TMessageReactionEvent[]>([]);
  messageId = observable.box<number>(-1);
  messageSenderId = observable.box<number>(-1);
  messageSenderGender = observable.box<string>("");

  /**
   * The message tables let the admin pick an era, so it comes from whichever
   * page's store loaded the rows on screen. Everywhere else the reaction footer
   * appears (report bubbles, auditor audit) the server fetches the conversation
   * as POST_2025, which is also the only era that has reactions at all.
   */
  getCurrentMessageEra = () => {
    const pageName = getCoreHistoryStore().getPageName();
    if (pageName === "USER_MESSAGES") {
      return getUserMessagesStore().messageEra.get().value;
    }
    if (pageName === "USER_MESSAGE_ID") {
      return getUserMessageIdStore().messageEra.get().value;
    }
    if (pageName === "USER_CONVERSATIONS") {
      return getUserConversationStore().messageEra.get().value;
    }
    return "POST_2025";
  };

  /**
   * A reaction can only come from the message's sender or its recipient, and a
   * conversation always has one user of each gender, so the reactor's gender is
   * the row's gender or its opposite. Mirrors the inference already done by
   * getMessagesData in the messages and conversation stores.
   */
  getReactionGender = (reactionSenderId: number) => {
    const messageSenderGender = this.messageSenderGender.get();
    if (reactionSenderId === this.messageSenderId.get()) {
      return messageSenderGender;
    }
    return messageSenderGender === Gender.Male ? Gender.Female : Gender.Male;
  };

  /**
   * The viewer splits into one panel per reactor, so a message both users
   * reacted to shows the two timelines side by side. The message sender's panel
   * comes first, and the events keep the newest-first order the API returned.
   */
  getEventPanels = () => {
    const events = this.events.get();
    const messageSenderId = this.messageSenderId.get();
    const senderIds: number[] = [];
    events.forEach((event) => {
      if (!senderIds.includes(event.senderId)) {
        senderIds.push(event.senderId);
      }
    });
    return senderIds
      .sort((first, second) => {
        if (first === messageSenderId) {
          return -1;
        }
        if (second === messageSenderId) {
          return 1;
        }
        return first - second;
      })
      .map((senderId) => ({
        senderId,
        isMessageSender: senderId === messageSenderId,
        events: events.filter((event) => event.senderId === senderId)
      }));
  };

  openHistory = async ({
    messageId,
    messageSenderId,
    messageSenderGender
  }: {
    messageId: number;
    messageSenderId: number;
    messageSenderGender: string;
  }) => {
    runInAction(() => {
      this.messageId.set(messageId);
      this.messageSenderId.set(messageSenderId);
      this.messageSenderGender.set(messageSenderGender);
      this.events.set([]);
      this.isLoading.set(true);
      this.isVisible.set(true);
    });

    const { data, code, status } = await apiCallGetMessageReactionHistory({
      messageId,
      messageEra: this.getCurrentMessageEra()
    });

    runInAction(() => {
      this.isLoading.set(false);
      this.events.set(data.events);
    });

    if (status === "ERROR") {
      runInAction(() => {
        this.isVisible.set(false);
      });
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  close = () => {
    runInAction(() => {
      this.isVisible.set(false);
      this.isLoading.set(false);
      this.events.set([]);
      this.messageId.set(-1);
      this.messageSenderId.set(-1);
      this.messageSenderGender.set("");
    });
  };
}

const getReactionHistoryStore = memoize(
  () => new ReactionHistoryStore(),
  () => 1
);
export default getReactionHistoryStore;
